import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Materials/MaterialActionPanel.css';

import {
  fetchMaterialsByProject,
  receiveMaterial,
  undoMaterialReceive,
  undoMaterialUsage
} from '../../../../../../../Redux/Projects/Material/materialSlice';

import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';
import { FaBoxOpen, FaArrowDown, FaArrowUp } from 'react-icons/fa';

const MaterialActionPanel = ({ materials = [], projectId, onUploadBill, onOpenLogUsageModal }) => {
  const dispatch = useDispatch();
  const { loading, submitting } = useSelector((state) => state.materials || {});

  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('PCS');
  const [action, setAction] = useState('Used'); // 'Used' | 'Received'

  // double-confirm undo (no browser confirm)
  const undoReceiveArmed = useRef(false);
  const undoUsageArmed = useRef(false);
  const undoTimer = useRef(null);

  const projectMaterials = useMemo(() => {
    return materials.filter((m) => String(m.projectId) === String(projectId));
  }, [materials, projectId]);

  const selectedMaterial = useMemo(() => {
    return projectMaterials.find((m) => String(m._id) === String(selectedMaterialId)) || null;
  }, [projectMaterials, selectedMaterialId]);

  const currentStock = useMemo(() => {
    if (!selectedMaterial) return null;
    const received = Number(selectedMaterial.received || 0);
    const used = Number(selectedMaterial.used || 0);
    return received - used;
  }, [selectedMaterial]);

  // keep unit synced to selected material (DON'T allow arbitrary unit changes unless you implement conversion)
  useEffect(() => {
    if (!selectedMaterial) return;
    setUnit(selectedMaterial.unit || 'PCS');
  }, [selectedMaterial]);

  const numericQty = Number(quantity);
  const hasQty = Number.isFinite(numericQty) && numericQty > 0;

  const isReceiveBlocked =
    !!selectedMaterial && !!selectedMaterial.hasUnbilledReceives && action === 'Received';

  const issueError = useMemo(() => {
    if (action !== 'Used') return '';
    if (!selectedMaterialId) return 'Select a material';
    if (!hasQty) return 'Enter a valid quantity';
    if ((currentStock ?? 0) <= 0) return 'Out of stock';
    if (numericQty > (currentStock ?? 0)) return `Insufficient stock. Available: ${currentStock} ${unit}`;
    return '';
  }, [action, selectedMaterialId, hasQty, currentStock, numericQty, unit]);

  const receiveError = useMemo(() => {
    if (action !== 'Received') return '';
    if (!selectedMaterialId) return 'Select a material';
    if (!hasQty) return 'Enter a valid quantity';
    if (isReceiveBlocked) return 'Bill pending — upload previous bill to receive more';
    return '';
  }, [action, selectedMaterialId, hasQty, isReceiveBlocked]);

  const blockSubmit = loading || submitting || !!issueError || !!receiveError;

  const handleMaterialChange = (e) => {
    const id = e.target.value;
    setSelectedMaterialId(id);
    // do NOT auto-reset quantity; user might be entering and just switching material by mistake
  };

  const resetUndoArms = () => {
    undoReceiveArmed.current = false;
    undoUsageArmed.current = false;
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
  };

  const armUndo = (type) => {
    resetUndoArms();

    if (type === 'receive') undoReceiveArmed.current = true;
    if (type === 'usage') undoUsageArmed.current = true;

    dispatch(
      showFeedback({
        type: 'warning',
        title: 'Confirm undo',
        message: 'Click the same Undo button again within 4 seconds to confirm.',
      })
    );

    undoTimer.current = setTimeout(() => {
      resetUndoArms();
    }, 4000);
  };

  const handleMaterialAction = async () => {
    if (!projectId) {
      dispatch(showFeedback({ type: 'error', title: 'No project', message: 'Project id missing.' }));
      return;
    }

    if (action === 'Used') {
      if (issueError) {
        dispatch(showFeedback({ type: 'error', title: 'Fix required', message: issueError }));
        return;
      }

      // ✅ No confirm popup. Issuing should always go through log-usage modal (that’s how stock updates correctly).
      onOpenLogUsageModal?.({
        material: selectedMaterial,
        prefill: {
          quantity: numericQty,
          unit: selectedMaterial?.unit || unit,
          lockQuantity: true,
        },
      });

      return;
    }

    // Received
    if (receiveError) {
      dispatch(showFeedback({ type: 'error', title: 'Fix required', message: receiveError }));
      return;
    }

    try {
      const res = await dispatch(
        receiveMaterial({
          materialId: selectedMaterialId,
          quantity: numericQty,
          projectId,
        })
      ).unwrap();

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Received',
          message: `Received ${numericQty} ${unit} of ${selectedMaterial?.name || 'material'}.`,
        })
      );

      // refresh lists (keep UI consistent)
      dispatch(fetchMaterialsByProject(projectId));

      // clear qty after successful receive
      setQuantity('');

      // ✅ no browser confirm. If bill is pending, just open upload flow.
      if (res?.hasUnbilledReceives && typeof onUploadBill === 'function') {
        onUploadBill({
          ...res,
          quantityToBill: numericQty,
          skipQuantityInBill: true,
        });
      }
    } catch (err) {
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Receive failed',
          message: err?.message || 'Failed to receive material.',
        })
      );
    }
  };

  const handleUndoReceive = async () => {
    if (!selectedMaterial) {
      dispatch(showFeedback({ type: 'error', title: 'Select material', message: 'Select a material first.' }));
      return;
    }

    if (!undoReceiveArmed.current) {
      armUndo('receive');
      return;
    }

    try {
      resetUndoArms();
      await dispatch(undoMaterialReceive({ materialId: selectedMaterial._id, projectId })).unwrap();
      dispatch(showFeedback({ type: 'success', title: 'Undo complete', message: 'Last receive action undone.' }));
      dispatch(fetchMaterialsByProject(projectId));
    } catch (err) {
      dispatch(showFeedback({ type: 'error', title: 'Undo failed', message: err?.message || 'Undo failed.' }));
    }
  };

  const handleUndoUsage = async () => {
    if (!selectedMaterial) {
      dispatch(showFeedback({ type: 'error', title: 'Select material', message: 'Select a material first.' }));
      return;
    }

    if (!undoUsageArmed.current) {
      armUndo('usage');
      return;
    }

    try {
      resetUndoArms();
      await dispatch(undoMaterialUsage({ materialId: selectedMaterial._id, projectId })).unwrap();
      dispatch(showFeedback({ type: 'success', title: 'Undo complete', message: 'Last issue action undone.' }));
      dispatch(fetchMaterialsByProject(projectId));
    } catch (err) {
      dispatch(showFeedback({ type: 'error', title: 'Undo failed', message: err?.message || 'Undo failed.' }));
    }
  };

  return (
    <div className="mx2-card">
      <div className="mx2-head">
        <div className="mx2-title">
          <FaBoxOpen style={{ marginRight: 8 }} /> Material Action Panel
        </div>

        {selectedMaterialId && currentStock !== null && (
          <div className="mx2-chip">
            <span className="mx2-chip__count">{currentStock}</span>
            <span className="mx2-chip__unit">{selectedMaterial?.unit || unit}</span>
          </div>
        )}
      </div>

      <div className="mx2-grid">
        {/* Material */}
        <div className="mx2-field mx2-col-6">
          <label>Material</label>
          <select value={selectedMaterialId} onChange={handleMaterialChange} disabled={loading || submitting}>
            <option value="">Select Material</option>
            {projectMaterials.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        {/* Quantity */}
        <div className="mx2-field mx2-col-3">
          <label>Quantity</label>
          <div className="mx2-qty">
            <input
              type="number"
              placeholder="0"
              value={quantity}
              min={1}
              onChange={(e) => setQuantity(e.target.value)} // ✅ no clamp to 0
              disabled={loading || submitting}
            />

            {/* unit is derived from material. Disable to prevent inconsistent stock unit bugs */}
            <select value={unit} disabled>
              <option value={unit}>{unit}</option>
            </select>
          </div>
        </div>

        {/* Action */}
        <div className="mx2-field mx2-col-3">
          <label>Action</label>
          <div className="mx2-seg">
            <button
              type="button"
              className={action === 'Used' ? 'is-active' : ''}
              onClick={() => setAction('Used')}
              aria-pressed={action === 'Used'}
              disabled={loading || submitting}
            >
              <FaArrowUp style={{ marginRight: 6 }} /> Issue
            </button>

            <button
              type="button"
              className={action === 'Received' ? 'is-active' : ''}
              onClick={() => setAction('Received')}
              aria-pressed={action === 'Received'}
              disabled={loading || submitting}
            >
              <FaArrowDown style={{ marginRight: 6 }} /> Receive
            </button>
          </div>

          {isReceiveBlocked && (
            <div className="mx2-warn">
              <span className="mx2-warn__icon">⚠</span>
              <span className="mx2-warn__text">Bill pending — upload previous bill to receive more.</span>
              {!!onUploadBill && selectedMaterial && (
                <button
                  type="button"
                  className="mx2-warn__cta"
                  onClick={() =>
                    onUploadBill({
                      ...selectedMaterial,
                      quantityToBill: Number(quantity) || 0,
                      skipQuantityInBill: true,
                    })
                  }
                >
                  Upload
                </button>
              )}
            </div>
          )}

          {/* inline errors */}
          {!!issueError && action === 'Used' && (
            <div className="mx2-warn" style={{ marginTop: 8 }}>
              <span className="mx2-warn__icon">⚠</span>
              <span className="mx2-warn__text">{issueError}</span>
            </div>
          )}

          {!!receiveError && action === 'Received' && (
            <div className="mx2-warn" style={{ marginTop: 8 }}>
              <span className="mx2-warn__icon">⚠</span>
              <span className="mx2-warn__text">{receiveError}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mx2-foot">
        {action === 'Received' && selectedMaterial?.lastReceiveCanUndo && (
          <button
            type="button"
            className="mx2-link"
            onClick={handleUndoReceive}
            disabled={loading || submitting}
          >
            ⏪ Undo last receive
          </button>
        )}

        {action === 'Used' && selectedMaterial?.lastUsageCanUndo && (
          <button
            type="button"
            className="mx2-link"
            onClick={handleUndoUsage}
            disabled={loading || submitting}
          >
            ⏪ Undo last issue
          </button>
        )}

        <button type="button" className="mx2-btn" onClick={handleMaterialAction} disabled={blockSubmit}>
          {loading || submitting ? 'Submitting…' : action === 'Used' ? 'Issue' : 'Receive'}
        </button>
      </div>
    </div>
  );
};

export default MaterialActionPanel;
